import React, {useEffect, useRef} from "react";
import {UserHomepageStateController} from "../../../../../containers/user-homepage";
import UserPlatformSingleEditionHomepage from "../../../../../components/app-specific/single-edition-homepage/sections/body";
import {UserHomepageControllerEvents, UserHomePageTypes, UserHomepageViewMode} from "../../../../../containers/user-homepage/models";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import UserPlatformPreviewNavigation from "../../../../../components/app-specific/single-edition-homepage/sections/preview-navigation";
import {routeFunctions} from "../../../../../../core/models/static/routes/app-routes";
import Api from "../../../../../../core/services/api/api";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import {useHistory, useParams} from "react-router-dom";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import useAccessLevels from "../../../../../hooks/use-access-levels";

const UserPlatformPhoneHomepageView = () => {
    const history = useHistory()
    const params = useParams()
    const stateControllerRef = useRef();
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('user-platform-phone-homepage-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const {packages} = useAccessLevels();
    const canManage = packages.clientPlatform.managePhoneHomepageEditions;

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.userPlatformPhoneHomepage(params?.versionId));
        configureBehavioralTool((tags) => tags.userPlatformPhoneHomepage(params?.versionId));
    }, [configureBehavioralTool, configureNotebookTags, params?.versionId]);

    /**
     * With each change in the controller or state:
     * - attaches an event listener for the selection of tabs so that the tab information can be loaded from the
     * server if necessary.
     * - attaches an event listener for changes in the view mode to sync the url params with the new view mode
     */
    useEffect(() => {
        stateControllerRef.current?.controller?.on(UserHomepageControllerEvents.onUnloadedTabSelected, getTabInformation);
        stateControllerRef.current?.controller?.on(UserHomepageControllerEvents.onViewModeChanged, setViewModeInUrl);
        const cleanup = () => {
            stateControllerRef.current?.controller?.off(UserHomepageControllerEvents.onUnloadedTabSelected, getTabInformation)
            stateControllerRef.current?.controller?.off(UserHomepageControllerEvents.onViewModeChanged, setViewModeInUrl);
        }
        return () => {
            cleanup();
        }
    }, [stateControllerRef])

    /**
     * With each change in the id of the params:
     * - loads the main-page data from the server.
     */
    useEffect(() => {
        getMainPageData().then();
    }, [params.versionId])

    /**
     * Fetches the main page data for the current edition of the user homepage from the server.
     * @return {Promise<void>}
     */
    const getMainPageData = async () => {
        const response = await Api.getHomepageEditionDetails(Number(params.versionId));
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            stateControllerRef.current?.controller?.loadHomepage({
                ...response?.data,
                isEditable: canManage,
            }, response.configuration);
        }
        if (!canManage && params.viewMode === UserHomepageViewMode.edit) {
            return history.replace(routeFunctions.userPlatform.phoneHomepage(params.versionId, UserHomepageViewMode.preview))
        }
    }

    /**
     * Fetches the given tab' information from the server and populates the state of the controller.
     * @param {UserHomepageEventEmitter} emitter
     * @param {number} tabId
     * @return {Promise<void>}
     */
    const getTabInformation = async (emitter, tabId) => {
        const response = await Api.getHomepageEditionDetails(Number(params.versionId), tabId);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            const newTab = response?.data?.tabs?.find(e => e.id === tabId);
            stateControllerRef.current?.controller?.loadTab(newTab, response.configuration);
        }
        if (!canManage && params.viewMode === UserHomepageViewMode.edit) {
            return history.replace(routeFunctions.userPlatform.phoneHomepage(params.versionId, UserHomepageViewMode.preview))
        }
    }

    /**
     * Changes the view mode of the edition based on the changes in controllers' view mode.
     * @param _
     * @param {UserHomepageViewMode} viewMode
     */
    const setViewModeInUrl = (_, viewMode) => {
        history.replace(routeFunctions.userPlatform.phoneHomepage(params.versionId, viewMode));
    }

    return (
        <>
            <UserHomepageStateController
                ref={stateControllerRef}
                viewMode={params.viewMode ?? UserHomepageViewMode.edit}
            >
                <UserPlatformSingleEditionHomepage
                    viewType={UserHomePageTypes.phone}
                />
                <UserPlatformPreviewNavigation/>
            </UserHomepageStateController>
        </>
    )
}


export default UserPlatformPhoneHomepageView;
